import { TagDescription } from "@reduxjs/toolkit/query";
import api from "src/shared/api";
import {
  EQueryTagTypes,
  ISwoolBaseResponse,
  ISwoolResponse,
} from "src/shared/types/api";
import { IBaseQueryParams } from "src/shared/types/query";
import { getProvidesTags } from "src/shared/utils/url/query.helpers";
import {
  IAgreementApiPathVariables,
  IAgreementItem,
  IPartnerComplexContractItem,
  IPartnerVirtualContractItem,
  IPostAgreementDTO,
  IPostPartnerComplexContractDTO,
  IPostPartnerVirtualContractDTO,
  IPutAgreementDTO,
  IVirtualContractBonusRanges,
  TPutPartnerComplexContractDTO,
  TPutPartnerVirtualContractDTO,
} from "../types";

const partnerContractsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getpartnerComplexContractsList: build.query<
      ISwoolResponse<IPartnerComplexContractItem[]>,
      {
        partner_id: number | string;
      } & Partial<IBaseQueryParams>
    >({
      query: ({ partner_id, ...params }) => {
        return {
          url: `api/partners/contracts/${partner_id}/list`,
          method: "GET",
          params,
        };
      },
      providesTags: (response) => {
        const tags: TagDescription<EQueryTagTypes>[] = [];
        if (!response) {
          return tags;
        }
        tags.push(
          { type: EQueryTagTypes.PARTNER_COMPLEX_CONTRACTS, id: "LIST" },
          ...getProvidesTags<IPartnerComplexContractItem>(
            response.data,
            EQueryTagTypes.PARTNER_COMPLEX_CONTRACTS,
          ),
        );

        return tags;
      },
    }),
    getPartnerComplexContract: build.query<
      IPartnerComplexContractItem,
      {
        partner_id: number | string;
        contract_id: number | string;
      }
    >({
      query: ({ partner_id, contract_id }) => {
        return {
          url: `api/partners/contracts/${partner_id}/${contract_id}`,
          method: "GET",
        };
      },
      transformResponse: (
        data: ISwoolResponse<IPartnerComplexContractItem>,
      ) => {
        return data.data;
      },
      providesTags: (response) => {
        const tags = [
          {
            type: EQueryTagTypes.PARTNER_COMPLEX_CONTRACTS,
            id: response?.id,
          },
        ];

        return tags;
      },
    }),
    putPartnerComplexContract: build.mutation<
      ISwoolResponse<IPartnerComplexContractItem>,
      TPutPartnerComplexContractDTO
    >({
      query: ({ id, partner_id, ...contract }) => {
        return {
          url: `api/partners/contracts/${partner_id}/${id}`,
          method: "PUT",
          body: contract,
        };
      },
      invalidatesTags: (response) => {
        const tags = response?.data.id
          ? [
              {
                type: EQueryTagTypes.PARTNER_COMPLEX_CONTRACTS,
                id: response?.data.id,
              },
            ]
          : [];

        return tags;
      },
    }),
    postPartnerComplexContract: build.mutation<
      ISwoolResponse<IPartnerComplexContractItem>,
      IPostPartnerComplexContractDTO
    >({
      query: (contract) => {
        return {
          url: `api/partners/contracts/`,
          method: "POST",
          body: contract,
        };
      },
      invalidatesTags: (response) => {
        const tags = response?.data
          ? [
              {
                type: EQueryTagTypes.PARTNER_COMPLEX_CONTRACTS,
                id: "LIST",
              },
            ]
          : [];

        return tags;
      },
    }),

    // virual contracts
    getpartnerVirtualContractsList: build.query<
      ISwoolResponse<IPartnerVirtualContractItem[]>,
      {
        partner_id: number | string;
      } & Partial<IBaseQueryParams>
    >({
      query: ({ partner_id, ...params }) => {
        return {
          url: `api/partners/${partner_id}/virtual-contracts`,
          method: "GET",
          params,
        };
      },
      providesTags: (response) => {
        const tags: TagDescription<EQueryTagTypes>[] = [];
        if (!response) {
          return tags;
        }
        tags.push(
          { type: EQueryTagTypes.PARTNER_VIRTUAL_CONTRACTS, id: "LIST" },
          ...getProvidesTags<IPartnerVirtualContractItem>(
            response.data,
            EQueryTagTypes.PARTNER_VIRTUAL_CONTRACTS,
          ),
        );

        return tags;
      },
    }),
    getPartnerVirtualContract: build.query<
      IPartnerVirtualContractItem,
      {
        partner_id: number | string;
        contract_id: number | string;
      }
    >({
      query: ({ partner_id, contract_id }) => {
        return {
          url: `api/partners/${partner_id}/virtual-contracts/${contract_id}`,
          method: "GET",
        };
      },
      transformResponse: (
        data: ISwoolResponse<IPartnerVirtualContractItem>,
      ) => {
        return data.data;
      },
      providesTags: (response) => {
        const tags = [
          {
            type: EQueryTagTypes.PARTNER_VIRTUAL_CONTRACTS,
            id: response?.id,
          },
        ];

        return tags;
      },
    }),
    putPartnerVirtualContract: build.mutation<
      ISwoolResponse<IPartnerVirtualContractItem>,
      TPutPartnerVirtualContractDTO
    >({
      query: ({ id, partner_id, ...contract }) => {
        return {
          url: `api/partners/${partner_id}/virtual-contracts/${id}`,
          method: "PUT",
          body: contract,
        };
      },
      invalidatesTags: (response) => {
        const tags = response?.data.id
          ? [
              {
                type: EQueryTagTypes.PARTNER_VIRTUAL_CONTRACTS,
                id: response?.data.id,
              },
              {
                type: EQueryTagTypes.PARTNER_VIRTUAL_CONTRACT_BONUSES,
                id: response?.data.id,
              },
            ]
          : [];

        return tags;
      },
    }),
    postPartnerVirtualContract: build.mutation<
      ISwoolResponse<IPartnerVirtualContractItem>,
      IPostPartnerVirtualContractDTO
    >({
      query: ({ partner_id, ...contract }) => {
        return {
          url: `api/partners/${partner_id}/virtual-contracts/`,
          method: "POST",
          body: contract,
        };
      },
      invalidatesTags: (response) => {
        const tags = response?.data
          ? [
              {
                type: EQueryTagTypes.PARTNER_VIRTUAL_CONTRACTS,
                id: "LIST",
              },
            ]
          : [];

        return tags;
      },
    }),
    getPartnerVirtualContractBonusRanges: build.query<
      IVirtualContractBonusRanges,
      {
        partner_id: number | string;
        contract_id: number | string;
      }
    >({
      query: ({ partner_id, contract_id }) => {
        return {
          url: `api/partners/${partner_id}/virtual-contracts/${contract_id}/bonus-percents-ranges`,
          method: "GET",
        };
      },
      transformResponse: (
        data: ISwoolResponse<IVirtualContractBonusRanges>,
      ) => {
        return data.data;
      },
      providesTags: (_response, _error, request) => {
        const tags = [
          {
            type: EQueryTagTypes.PARTNER_VIRTUAL_CONTRACT_BONUSES,
            id: request?.contract_id,
          },
        ];

        return tags;
      },
    }),
    deletePartnerVirtualContract: build.mutation<
      void,
      { partnerId: string; contract_id: string }
    >({
      query: ({ partnerId, contract_id }) => {
        return {
          url: `/api/partners/${partnerId}/virtual-contracts/${contract_id}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: () => [
        { type: EQueryTagTypes.PARTNER_VIRTUAL_CONTRACTS, id: "LIST" },
      ],
    }),
    linkWithComplexContract: build.mutation<
      IPartnerVirtualContractItem,
      {
        partner_id: number;
        complex_contract_id: number;
        virtual_contract_id: number;
      }
    >({
      query: ({ partner_id, complex_contract_id, virtual_contract_id }) => {
        return {
          url: `/api/partners/contracts/${partner_id}/${complex_contract_id}/link/${virtual_contract_id}`,
          method: "PATCH",
        };
      },
      invalidatesTags: (_response, error, request) => {
        return error
          ? []
          : [
              {
                type: EQueryTagTypes.PARTNER_VIRTUAL_CONTRACTS,
                id: request?.virtual_contract_id,
              },
            ];
      },
    }),
    unlinkWithComplexContract: build.mutation<
      IPartnerVirtualContractItem,
      {
        partner_id: number;
        complex_contract_id: number;
        virtual_contract_id: number;
      }
    >({
      query: ({ partner_id, complex_contract_id, virtual_contract_id }) => {
        return {
          url: `/api/partners/contracts/${partner_id}/${complex_contract_id}/unlink/${virtual_contract_id}`,
          method: "PATCH",
        };
      },
      invalidatesTags: (_response, error, request) => {
        return error
          ? []
          : [
              {
                type: EQueryTagTypes.PARTNER_VIRTUAL_CONTRACTS,
                id: request?.virtual_contract_id,
              },
            ];
      },
    }),
    getAgreementItem: build.query<IAgreementItem, IAgreementApiPathVariables>({
      query: ({ contract_id, partner_id, agreement_id }) => {
        return {
          url: `api/partners/contracts/${partner_id}/${contract_id}/agreements/${agreement_id}`,
          method: "GET",
        };
      },
      transformResponse: (data: ISwoolResponse<IAgreementItem>) => {
        return data.data;
      },
      providesTags: (response) => {
        const tags = response
          ? [
              {
                type: EQueryTagTypes.PARTNER_AGREEMENTS,
                id: response.id,
              },
            ]
          : [];

        return tags;
      },
    }),
    postAgreement: build.mutation<
      ISwoolResponse<IAgreementItem>,
      IPostAgreementDTO
    >({
      query: ({ contract_id, partner_id, ...agreement }) => {
        return {
          url: `api/partners/contracts/${partner_id}/${contract_id}/agreements/`,
          method: "POST",
          body: agreement,
        };
      },
      invalidatesTags: (response) => {
        const tags = response?.data
          ? [
              {
                type: EQueryTagTypes.PARTNER_AGREEMENTS,
                id: "LIST",
              },
              {
                type: EQueryTagTypes.PARTNER_COMPLEX_CONTRACTS,
                id: response.data.contract_id,
              },
            ]
          : [];

        return tags;
      },
    }),
    patchAgreement: build.mutation<
      ISwoolResponse<IAgreementItem>,
      IPutAgreementDTO
    >({
      query: ({ contract_id, partner_id, agreement_id, ...agreement }) => {
        return {
          url: `api/partners/contracts/${partner_id}/${contract_id}/agreements/${agreement_id}`,
          method: "PATCH",
          body: agreement,
        };
      },
      invalidatesTags: (response) => {
        const tags = response?.data
          ? [
              {
                type: EQueryTagTypes.PARTNER_AGREEMENTS,
                id: response.data.id,
              },
              {
                type: EQueryTagTypes.PARTNER_COMPLEX_CONTRACTS,
                id: response.data.contract_id,
              },
            ]
          : [];

        return tags;
      },
    }),
    deleteAgreement: build.mutation<unknown, IAgreementApiPathVariables>({
      query: ({ contract_id, partner_id, agreement_id }) => {
        return {
          url: `/api/partners/contracts/${partner_id}/${contract_id}/agreements/${agreement_id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (response, _error, request) => {
        return response
          ? [
              { type: EQueryTagTypes.PARTNER_AGREEMENTS, id: "LIST" },
              {
                type: EQueryTagTypes.PARTNER_COMPLEX_CONTRACTS,
                id: request.contract_id,
              },
            ]
          : [];
      },
    }),
    postAgreementAttachment: build.mutation<
      ISwoolResponse<IAgreementItem>,
      IAgreementApiPathVariables & {
        file: FormData;
      }
    >({
      query: ({ contract_id, partner_id, agreement_id, file }) => {
        return {
          url: `/api/partners/contracts/${partner_id}/${contract_id}/agreements/${agreement_id}/file`,
          method: "POST",
          body: file,
          formData: true,
        };
      },
      invalidatesTags: (response) => {
        const tags = response?.data
          ? [
              {
                type: EQueryTagTypes.PARTNER_AGREEMENTS,
                id: response.data.id,
              },
              {
                type: EQueryTagTypes.PARTNER_COMPLEX_CONTRACTS,
                id: response.data.contract_id,
              },
            ]
          : [];

        return tags;
      },
    }),
    deleteAgreementAttachment: build.mutation<
      unknown,
      IAgreementApiPathVariables
    >({
      query: ({ contract_id, partner_id, agreement_id }) => {
        return {
          url: `/api/partners/contracts/${partner_id}/${contract_id}/agreements/${agreement_id}/file`,
          method: "DELETE",
        };
      },
      invalidatesTags: (response, _error, request) => {
        return response
          ? [
              {
                type: EQueryTagTypes.PARTNER_AGREEMENTS,
                id: request.agreement_id,
              },
              {
                type: EQueryTagTypes.PARTNER_COMPLEX_CONTRACTS,
                id: request.contract_id,
              },
            ]
          : [];
      },
    }),
    attachContractOriginal: build.mutation<
      ISwoolBaseResponse<void>,
      { partner_id: number; contract_id: number; file: File | string | null }
    >({
      query: ({ partner_id, contract_id, file }) => {
        const formData = new FormData();

        if (file) {
          formData.append("file", file);
        }

        return {
          url: `/api/partners/contracts/${partner_id}/${contract_id}/contract-original`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { contract_id }) => [
        { type: EQueryTagTypes.PARTNER_COMPLEX_CONTRACTS, id: contract_id },
      ],
    }),
    deleteContractOriginal: build.mutation<
      ISwoolBaseResponse<void>,
      { partner_id: number; contract_id: number }
    >({
      query: ({ partner_id, contract_id }) => {
        return {
          url: `/api/partners/contracts/${partner_id}/${contract_id}/contract-original`,
          method: "DELETE",
        };
      },
    }),
    attachTaxCertificate: build.mutation<
      ISwoolBaseResponse<void>,
      { partner_id: number; contract_id: number; file: File | string | null }
    >({
      query: ({ partner_id, contract_id, file }) => {
        const formData = new FormData();

        if (file) {
          formData.append("file", file);
        }

        return {
          url: `/api/partners/contracts/${partner_id}/${contract_id}/tax-certificate`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { contract_id }) => [
        { type: EQueryTagTypes.PARTNER_COMPLEX_CONTRACTS, id: contract_id },
      ],
    }),
    deleteTaxCertificate: build.mutation<
      ISwoolBaseResponse<void>,
      { partner_id: number; contract_id: number }
    >({
      query: ({ partner_id, contract_id }) => {
        return {
          url: `/api/partners/contracts/${partner_id}/${contract_id}/tax-certificate`,
          method: "DELETE",
        };
      },
    }),
    getPartnerContractDocx: build.query<
      string,
      {
        partner_id: number | string;
        contract_id: number | string;
      }
    >({
      query: ({ partner_id, contract_id }) => {
        return {
          url: `api
/partners/contracts/${partner_id}/${contract_id}/template`,
          method: "GET",
          responseHandler: (response) => response.text(),
        };
      },
    }),
    getPartnerAgreementTemplate: build.query<
      string,
      {
        partner_id: number | string;
        contract_id: number | string;
      }
    >({
      query: ({ partner_id, contract_id }) => {
        return {
          url: `api/partners/contracts/${partner_id}/${contract_id}/agreements/template`,
          method: "GET",
          responseHandler: (response) => response.text(),
        };
      },
    }),
    getPartnerAgreementTemplateEdit: build.query<
      string,
      {
        partner_id: number | string;
        contract_id: number | string;
        agreement_id: number | string;
      }
    >({
      query: ({ partner_id, contract_id, agreement_id }) => {
        return {
          url: `api/partners/contracts/${partner_id}/${contract_id}/agreements/${agreement_id}/template`,
          method: "GET",
          responseHandler: (response) => response.text(),
        };
      },
    }),
    downloadPartnerContractDocx: build.mutation({
      query: ({ partner_id, contract_id }) => {
        return {
          url: `api
/partners/contracts/${partner_id}/${contract_id}/template/download`,
          method: "GET",
          responseHandler: (response) => response.blob(),
        };
      },
      transformResponse: (blob: Blob) => {
        return URL.createObjectURL(blob);
      },
    }),
    downloadPartnerTemplate: build.mutation<
      string,
      {
        partner_id: string;
        contract_id: string;
        agreement_id: string;
      }
    >({
      query: ({ partner_id, contract_id, agreement_id }) => ({
        url: `/api/partners/contracts/${partner_id}/${contract_id}/agreements/${agreement_id}/template/download`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (blob: Blob) => URL.createObjectURL(blob),
    }),
  }),
});

export const {
  useGetpartnerComplexContractsListQuery,
  useGetPartnerComplexContractQuery,
  usePutPartnerComplexContractMutation,
  usePostPartnerComplexContractMutation,
  // virtual
  useGetPartnerVirtualContractQuery,
  useGetpartnerVirtualContractsListQuery,
  usePutPartnerVirtualContractMutation,
  usePostPartnerVirtualContractMutation,
  useGetPartnerVirtualContractBonusRangesQuery,
  useDeletePartnerVirtualContractMutation,
  useLinkWithComplexContractMutation,
  useUnlinkWithComplexContractMutation,
  // agreements
  useGetAgreementItemQuery,
  usePostAgreementMutation,
  usePatchAgreementMutation,
  useDeleteAgreementMutation,
  usePostAgreementAttachmentMutation,
  useDeleteAgreementAttachmentMutation,
  useAttachContractOriginalMutation,
  useDeleteContractOriginalMutation,
  useAttachTaxCertificateMutation,
  useDeleteTaxCertificateMutation,
  useGetPartnerContractDocxQuery,
  useGetPartnerAgreementTemplateQuery,
  useGetPartnerAgreementTemplateEditQuery,
  useLazyGetPartnerAgreementTemplateQuery,
  useDownloadPartnerContractDocxMutation,
  useDownloadPartnerTemplateMutation,
} = partnerContractsApi;
